import { useState, useCallback, useEffect } from 'react';
import type { Arealplan } from '@origo/arealplan';
import { useArealplanClient } from '@origo/arealplan';
import type { PanelComponentProps } from '../../components/AdminSidePanel';
import { PlanSearch } from './PlanSearch';
import { AdminPage } from './components/AdminPage';
import { useMapViewContext, selectedPolygonSymbol } from '@origo/map-context';
import Graphic from '@arcgis/core/Graphic';
import { usePlanMetadata } from './hooks/usePlanMetadata';
import { getPlanPublishStatus } from '../filter/filterUtils';

/** Søkevisning: 50 vw. Søk + avansert filter: 70 vw (plass til filter-kolonne). Adminredigering: 75 vw. */
const panelWidth = (vw: number) => Math.floor(window.innerWidth * vw / 100);

interface AdminViewProps extends PanelComponentProps {
    selectedPlan: Arealplan | null;
    onSelectPlan: (plan: Arealplan | null) => void;
}

export function AdminView({ onClose, onPanelWidthChange, eiendommerUrl, selectedPlan, onSelectPlan }: AdminViewProps) {
    const [filterOpen, setFilterOpen] = useState(false);
    const { mapView } = useMapViewContext();
    const arealplanClient = useArealplanClient();
    const { metadata, publishPlan, unpublishPlan, registrerRedigering, updateBeskrivelse, updatePlanregisterlink, updateGjeldendeBestemmelser } = usePlanMetadata();

    const handleSelect = useCallback((plan: Arealplan) => {
        onSelectPlan(plan);
    }, [onSelectPlan]);

    const handleZoomTo = useCallback(async (plan: Arealplan) => {
        if (!mapView) return;
        const geometry = await arealplanClient.arealplaner.getGeometry(plan);
        if (!geometry) return;
        mapView.graphics.removeAll();
        mapView.graphics.add(new Graphic({
            geometry: geometry as __esri.Geometry,
            symbol: selectedPolygonSymbol() as any,
        }));
        mapView.goTo(geometry as __esri.Geometry);
    }, [mapView, arealplanClient]);

    // Signal width change based on current view + filter state
    useEffect(() => {
        if (selectedPlan) {
            onPanelWidthChange?.(panelWidth(75));
        } else if (filterOpen) {
            onPanelWidthChange?.(panelWidth(70));
        } else {
            onPanelWidthChange?.(panelWidth(50));
        }
    }, [selectedPlan, filterOpen, onPanelWidthChange]);

    if (selectedPlan) {
        const planMeta = metadata.get(selectedPlan.planId);
        const isPublished = getPlanPublishStatus(planMeta) === 'publisert';
        return (
            <AdminPage
                plan={selectedPlan}
                onBack={() => onSelectPlan(null)}
                onClose={onClose}
                onPublish={publishPlan}
                onUnpublish={unpublishPlan}
                onEdited={registrerRedigering}
                onUpdateBeskrivelse={(beskrivelse) => updateBeskrivelse(selectedPlan.planId, beskrivelse, planMeta?.kommunenummer)}
                onUpdatePlanregisterlink={(url) => updatePlanregisterlink(selectedPlan.planId, url, planMeta?.kommunenummer)}
                onUpdateGjeldendeBestemmelser={(url) => updateGjeldendeBestemmelser(selectedPlan.planId, url, planMeta?.kommunenummer)}
                eiendommerUrl={eiendommerUrl}
                isPublished={isPublished}
                sistRedigertDato={planMeta?.sistRedigertDato}
                planMetadata={planMeta}
            />
        );
    }

    return (
        <PlanSearch
            onSelect={handleSelect}
            onZoomTo={handleZoomTo}
            onFilterToggle={setFilterOpen}
            planMetadata={metadata}
        />
    );
}
